import { useMutation } from '@apollo/client';
import { updateWidgetConfigurationMutation } from '@dixa/lib-integrations-partner-widgets';
import {
  Avatar,
  Button,
  FlexBox,
  Switch,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type { CustomCardIntegration } from 'lib-shared-types-api';
import { ChangeEvent, useState } from 'react';

import {
  UpdateWidgetConfigurationArg,
  UpdateWidgetConfigurationResponse,
} from 'apis/partnerIntegrations';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { getTooltipContent } from '../../../utils/integrationTooltipContent/integrationTooltipContent';
import {
  getIntegration,
  IntegrationType,
} from '../../../utils/useIntegrations/integrations';

import styles from './integrationsCard.module.scss';

export type IntegrationsCardItem = {
  id: string;
  label: string;
  integrationType: IntegrationType;
  integrationId: string;
  description: string;
  cardType: string;
  enabled?: boolean;
};

type IntegrationsCardProps = {
  integrationsCard: IntegrationsCardItem;
};

export const IntegrationsCard = ({
  integrationsCard,
}: IntegrationsCardProps): JSX.Element => {
  const navigate = useAppNavigate();
  const notify = useNotifications();

  const { id, label, enabled, cardType, integrationType, description } =
    integrationsCard;
  const { icon, iconColors } = getIntegration({ id: cardType });

  const [switchState, setSwitchState] = useState(enabled);

  const [updateWidgetConfiguration] = useMutation<
    UpdateWidgetConfigurationResponse,
    UpdateWidgetConfigurationArg
  >(updateWidgetConfigurationMutation);

  const handleSwitchChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const isEnabled = event.target.checked;
    setSwitchState(isEnabled);

    const response = await updateWidgetConfiguration({
      variables: {
        id,
        enabled: isEnabled,
        name: label,
      },
    });

    if ('errors' in response) {
      notify(
        {
          title: 'Error',
          text: `"${label}" could not be ${
            isEnabled ? 'enabled' : 'disabled'
          }.`,
          icon: 'i-error',
          time: 5000,
        },
        NotificationType.Error,
      );
    } else {
      notify(
        {
          title: 'Success',
          text: `"${label}" has been ${isEnabled ? 'enabled' : 'disabled'}.`,
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );
    }
  };

  const handleEditClick = () => {
    if (integrationType === 'customCard') {
      const customCardType =
        cardType as CustomCardIntegration['integration_type'];

      ampli.settingsIntegrationsItemClicked({
        integrationsId: id,
      });

      navigate({
        path: `/settings/integrations/${customCardType}-card/edit/:id`,
        params: { path: { id } },
      });
    }

    if (integrationType === 'partnerWidget') {
      const partnerWidgetType = cardType.toLowerCase();

      ampli.partnerIntegrationsSettingsOverviewViewed({
        widgetId: id,
        widgetType: partnerWidgetType,
      });

      navigate({
        path: `/settings/integrations/partner-widget/:type/:id`,
        params: {
          path: {
            id,
            type: partnerWidgetType,
          },
        },
      });
    }
  };

  return (
    <FlexBox
      key={id}
      flexDirection="row"
      gap="300"
      padding="400 250"
      className={styles.integrationsCard}
    >
      <FlexBox padding="0 0 0 300">
        <Tooltip content={getTooltipContent({ cardType, integrationType })}>
          <Avatar size="40" icon={icon} color={iconColors} />
        </Tooltip>
      </FlexBox>

      <FlexBox flexDirection="column" gap="300" width="100%">
        <Text.Heading variant="heading-3">{label}</Text.Heading>

        <Text.Paragraph className={styles.integrationsCardDescription}>
          {description}
        </Text.Paragraph>

        <FlexBox
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <FlexBox gap="200">
            <Button icon="pen" variant="secondary" onClick={handleEditClick}>
              Edit
            </Button>

            {integrationType === 'customCard' && (
              <Button
                icon="play"
                variant="secondary"
                onClick={() =>
                  navigate({
                    path: `/settings/integrations/${
                      cardType as CustomCardIntegration['integration_type']
                    }-card/test/:id`,
                    params: { path: { id } },
                  })
                }
              >
                Test
              </Button>
            )}
          </FlexBox>

          {integrationType === 'partnerWidget' && (
            <Switch
              checked={switchState}
              name="enabled-switch"
              value=""
              onChange={handleSwitchChange}
            />
          )}
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
};
